import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { useZeoContent } from "../lib/ZeoContentContext";

const DISMISS_KEY = "frida_banner_dismissed";
const SHOW_DELAY_MS = 1100;

// Panelde "Özel Gün Teması" aktif ve duyuru şeridi açıksa, siteyi her
// ziyaret açılışında kısa bir gecikmeyle ortada bir popup olarak belirir —
// aynı mesaj bir kez kapatılınca o oturum boyunca tekrar gösterilmez; yeni
// bir tema/mesaj seçilirse otomatik olarak tekrar görünür.
export default function AnnouncementBanner() {
  const { theme, loaded } = useZeoContent();
  const [dismissedText, setDismissedText] = useState(() => sessionStorage.getItem(DISMISS_KEY));
  const [visible, setVisible] = useState(false);

  const shouldShow = loaded && theme.banner_enabled && !!theme.banner_text && dismissedText !== theme.banner_text;

  useEffect(() => {
    if (!shouldShow) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldShow, theme.banner_text]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, theme.banner_text);
    setDismissedText(theme.banner_text);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Karartılmış, bulanık arka plan */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleDismiss}
            className="fixed inset-0 bg-black/55 backdrop-blur-sm z-[100]"
            data-testid="announcement-backdrop"
          />

          {/* Popup kart */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 16 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto relative w-full max-w-md rounded-[28px] overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.4)]"
              style={{ background: `linear-gradient(150deg, ${theme.primary}, ${theme.accent})` }}
              data-testid="announcement-popup"
            >
              {/* Dekoratif bulanık daireler */}
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/15 blur-2xl pointer-events-none" />
              <div className="absolute -bottom-14 -left-10 w-48 h-48 rounded-full bg-black/10 blur-3xl pointer-events-none" />

              <button
                onClick={handleDismiss}
                aria-label="Kapat"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors z-10"
                data-testid="announcement-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="relative px-8 py-10 sm:py-12 text-center flex flex-col items-center gap-5">
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner"
                >
                  <Sparkles className="w-7 h-7 text-white" />
                </motion.div>

                <p className="font-serif text-white text-2xl sm:text-[28px] leading-snug text-balance">
                  {theme.banner_text}
                </p>

                <a
                  href="#urunler"
                  onClick={handleDismiss}
                  className="mt-1 inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#222222] rounded-full font-semibold text-sm hover:scale-105 active:scale-95 transition-transform"
                  data-testid="announcement-cta"
                >
                  Aranjmanları Keşfet
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
